import type { ReactNode } from 'react'

import { Star } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useWatchlist } from '@/hooks/use-watchlist'

interface WatchButtonProps {
    id: string
    ticker: string
    variant?: 'icon' | 'button'
    className?: string
}

const WatchButton = ({ id, ticker, variant = 'icon', className }: WatchButtonProps): ReactNode => {
    const { has, toggle, ready } = useWatchlist()
    const active = ready && has(id)
    const label = active ? `Remove ${ticker} from watchlist` : `Add ${ticker} to watchlist`

    if (variant === 'button') {
        return (
            <button
                type='button'
                aria-pressed={active}
                onClick={() => toggle(id)}
                className={cn(
                    'inline-flex h-9 items-center gap-2 rounded-lg border px-3 text-[13px] font-medium transition-colors',
                    active
                        ? 'border-primary/40 bg-primary/10 text-primary'
                        : 'border-border bg-secondary text-foreground hover:bg-accent',
                    className
                )}
            >
                <Star aria-hidden='true' className={cn('size-4', active && 'fill-current')} />
                {active ? 'Watching' : 'Watch'}
            </button>
        )
    }

    return (
        <button
            type='button'
            aria-pressed={active}
            aria-label={label}
            title={label}
            onClick={event => {
                event.preventDefault()
                event.stopPropagation()
                toggle(id)
            }}
            className={cn(
                'inline-flex size-7 items-center justify-center rounded-md transition-colors',
                active ? 'text-primary' : 'text-muted-foreground/60 hover:bg-secondary hover:text-foreground',
                className
            )}
        >
            <Star aria-hidden='true' className={cn('size-4', active && 'fill-current')} />
        </button>
    )
}

export default WatchButton
